/**
 * Xero OAuth callback relay (issue #91).
 *
 * Xero redirects the browser here with ?code&state. The page posts them to
 * the authenticated admin API (the state is a CSRF check; auth is the
 * normal Firebase token), then either bounces back to Settings or — when
 * the authorisation covers multiple Xero organisations — shows the picker.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import {
  completeXeroCallback,
  pickXeroTenant,
  type XeroTenant,
} from "../lib/xero-api";
import page from "./page.module.css";
import styles from "./xero-callback.module.css";

export default function XeroCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<XeroTenant[] | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    document.title = "Connecting Xero — Rental Admin";
  }, []);

  useEffect(() => {
    if (ran.current) return; // authorization codes are single-use
    ran.current = true;

    const code = params.get("code") ?? "";
    const state = params.get("state") ?? "";
    if (!code || !state) {
      setError(
        "Missing code or state — start the connection again from Settings.",
      );
      return;
    }
    completeXeroCallback(code, state)
      .then((res) => {
        if (res.status === "active") {
          navigate("/settings?section=integrations", { replace: true });
        } else {
          setTenants(res.tenants ?? []);
        }
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = async (tenantId: string) => {
    setPicking(tenantId);
    try {
      await pickXeroTenant(tenantId);
      navigate("/settings?section=integrations", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPicking(null);
    }
  };

  return (
    <div className={page.page}>
      <div className={page.content}>
        <h1 className={styles.title}>Connecting Xero</h1>

        {error && (
          <>
            <Banner variant="error" className={styles.banner}>
              {error}
            </Banner>
            <Button
              variant="secondary"
              onClick={() => navigate("/settings?section=integrations")}
            >
              Back to Settings
            </Button>
          </>
        )}

        {!error && tenants === null && (
          <p className={styles.status}>Finishing the connection…</p>
        )}

        {!error && tenants !== null && (
          <div className={styles.picker}>
            <p className={styles.pickerLead}>
              Your Xero login has access to more than one organisation. Pick the
              one this workspace should invoice through:
            </p>
            <ul className={styles.pickerList}>
              {tenants.map((t) => (
                <li key={t.tenantId}>
                  <Button
                    variant="secondary"
                    className={styles.pickerButton}
                    loading={picking === t.tenantId}
                    onClick={() => pick(t.tenantId)}
                  >
                    {t.tenantName}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
